import React, { useState, useMemo, useEffect } from 'react';
import { Users, UserCheck, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import type { ApprovalWorkflow } from '@/types/workflow';
import type { Employee } from '@/types/timesheet';

interface WorkflowAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflow: ApprovalWorkflow;
  employees: Employee[];
  onAssign: (workflowId: string, assignmentType: 'all' | 'selected', selectedEmployeeIds?: number[]) => Promise<void>;
}

export function WorkflowAssignDialog({
  open,
  onOpenChange,
  workflow,
  employees,
  onAssign,
}: WorkflowAssignDialogProps) {
    const [assignmentType, setAssignmentType] = useState<'all' | 'selected'>('all');
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

  // Get employees already assigned to this workflow
  const alreadyAssignedIds = useMemo(() => {
    return employees
      .filter((emp) => emp.approvalWorkflowId === workflow.id)
      .map((emp) => emp.id);
  }, [employees, workflow.id]);

  // Pre-select already assigned employees when dialog opens
  useEffect(() => {
    if (open && alreadyAssignedIds.length > 0) {
      setSelectedEmployeeIds(alreadyAssignedIds);
      // If there are already assigned users, default to "selected" mode
      setAssignmentType('selected');
    }
  }, [open, alreadyAssignedIds]);

  // Filter employees based on search query
  const filteredEmployees = useMemo(() => {
    if (!searchQuery.trim()) return employees;
    
    const query = searchQuery.toLowerCase();
    return employees.filter(
      (emp) =>
        emp.name.toLowerCase().includes(query) ||
        emp.email?.toLowerCase().includes(query) ||
        emp.department?.toLowerCase().includes(query)
    );
  }, [employees, searchQuery]);

    const handleToggleEmployee = (employeeId: number) => {
      setSelectedEmployeeIds((prev) =>
        prev.includes(employeeId)
          ? prev.filter((id) => id !== employeeId)
          : [...prev, employeeId]
      );
    };

  const handleSelectAll = () => {
    if (selectedEmployeeIds.length === filteredEmployees.length) {
      setSelectedEmployeeIds([]);
    } else {
      setSelectedEmployeeIds(filteredEmployees.map((emp) => emp.id));
    }
  };

  const handleAssign = async () => {
    setIsSubmitting(true);
    try {
      await onAssign(
        workflow.id,
        assignmentType,
        assignmentType === 'selected' ? selectedEmployeeIds : undefined
      );
      onOpenChange(false);
      // Reset state
      setAssignmentType('all');
      setSelectedEmployeeIds([]);
      setSearchQuery('');
    } catch (error) {
      console.error('Failed to assign workflow:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = assignmentType === 'all' || selectedEmployeeIds.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Assign Workflow to Users</DialogTitle>
          <DialogDescription>
            Assign "{workflow.name}" to users. This workflow will be added to their existing workflow assignments.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
        <div className="space-y-4">
          {/* Assignment Type Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Assignment Scope</Label>
            <RadioGroup value={assignmentType} onValueChange={(value) => setAssignmentType(value as 'all' | 'selected')}>
              <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors">
                <RadioGroupItem value="all" id="all" className="mt-1" />
                <Label htmlFor="all" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="font-medium">Assign to All Users</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    This workflow will be assigned to all {employees.length} users in the system
                  </p>
                </Label>
              </div>

              <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent cursor-pointer transition-colors">
                <RadioGroupItem value="selected" id="selected" className="mt-1" />
                <Label htmlFor="selected" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4" />
                    <span className="font-medium">Assign to Selected Users</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose specific users to assign this workflow to
                  </p>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* User Selection (only shown when 'selected' is chosen) */}
          {assignmentType === 'selected' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Select Users ({selectedEmployeeIds.length} selected)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                >
                  {selectedEmployeeIds.length === filteredEmployees.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or department..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Employee List - Fixed height with scroll */}
              <div className="border rounded-lg max-h-[200px] overflow-y-auto">
                <div className="space-y-1 p-2">
                  {filteredEmployees.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No users found matching your search
                    </div>
                  ) : (
                    filteredEmployees.map((employee) => (
                      <div
                        key={employee.id}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-accent cursor-pointer"
                        onClick={() => handleToggleEmployee(employee.id)}
                      >
                        <Checkbox
                          checked={selectedEmployeeIds.includes(employee.id)}
                          onCheckedChange={() => handleToggleEmployee(employee.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{employee.name}</span>
                            {employee.department && (
                              <Badge variant="outline" className="text-xs">
                                {employee.department}
                              </Badge>
                            )}
                            {alreadyAssignedIds.includes(employee.id) && (
                              <Badge variant="secondary" className="text-xs bg-green-100 text-green-800">
                                Already assigned
                              </Badge>
                            )}
                          </div>
                          {employee.email && (
                            <p className="text-sm text-muted-foreground truncate">
                              {employee.email}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <strong>Note:</strong> This workflow will be added to users' existing workflow assignments. 
              Users can have multiple workflows assigned to them.
            </p>
          </div>
        </div>
        </div>

        <DialogFooter className="shrink-0 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleAssign} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? 'Assigning...' : `Assign to ${assignmentType === 'all' ? 'All Users' : `${selectedEmployeeIds.length} User(s)`}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
